import { ReloadIcon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import { Button, UnstyledButton, Text } from '@/shared/ui';
import type { SuggestionStatus } from './useSuggestionPicker';

interface SuggestionPanelProps {
  status: SuggestionStatus;
  options: string[];
  onSelect: (value: string) => void;
  onReroll: () => void;
  optionClassName: string;
}

/**
 * Renders the body of the suggestion picker for the current status: a thinking
 * indicator, the clickable option rows plus a re-roll footer, or an error state
 * with a retry affordance. Shared by the desktop popover and the mobile sheet.
 *
 * @param props - Current status, options, and the select/reroll callbacks.
 * @returns The status-appropriate panel content.
 */
export function SuggestionPanel({
  status,
  options,
  onSelect,
  onReroll,
  optionClassName,
}: SuggestionPanelProps) {
  const { t } = useTranslation('features/properties');

  if (status === 'loading') {
    return (
      <div
        data-testid="suggestion-thinking"
        className="flex items-center gap-(--space-2) px-(--space-3) py-(--space-3) text-(--text-tertiary)"
        role="status"
        aria-live="polite"
      >
        <Text as="span" className="flex gap-(--space-1)" aria-hidden>
          <Text as="span" className="size-1.5 rounded-full bg-(--brand-accent) animate-pulse [animation-delay:0ms]" />
          <Text as="span" className="size-1.5 rounded-full bg-(--brand-accent) animate-pulse [animation-delay:150ms]" />
          <Text as="span" className="size-1.5 rounded-full bg-(--brand-accent) animate-pulse [animation-delay:300ms]" />
        </Text>
        <Text as="span" className="text-xs">{t('propertyDrawer.suggestions.thinking')}</Text>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col items-start gap-(--space-2) px-(--space-3) py-(--space-3)">
        <Text as="span" className="text-xs text-(--status-error)">
          {t('propertyDrawer.suggestions.error')}
        </Text>
        <Button
          variant="none"
          disableSize
          type="button"
          onClick={onReroll}
          className="inline-flex items-center gap-(--space-1-5) text-xs font-semibold text-(--brand-accent) hover:underline"
        >
          <ReloadIcon width={12} height={12} aria-hidden /> {t('propertyDrawer.suggestions.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div role="menu" className="flex flex-col">
      {options.map((opt, i) => (
        <UnstyledButton
          key={`${i}-${opt}`}
          type="button"
          role="menuitem"
          onClick={() => onSelect(opt)}
          className={optionClassName}
        >
          {opt}
        </UnstyledButton>
      ))}
      <Button
        variant="none"
        disableSize
        type="button"
        onClick={onReroll}
        className="mt-(--space-1) inline-flex items-center justify-center gap-(--space-1-5) border-t border-(--border-subtle) px-(--space-3) py-(--space-2-5) text-xs font-semibold text-(--brand-accent) hover:bg-(--surface-hover)"
      >
        <ReloadIcon width={12} height={12} aria-hidden /> {t('propertyDrawer.suggestions.suggestNew')}
      </Button>
    </div>
  );
}
